/**
 * @fileoverview Public landing-page footer — 12-col grid with a brand
 * column on the left (logo, blurb, contact list) and three link columns
 * on the right (Product / Resources / Company). The bottom bar carries
 * the copyright + a small release-pill so the version is always visible
 * without taking nav space.
 *
 * Extracted verbatim out of the original Landing/index.tsx.
 */
import { Link } from 'react-router-dom'
import { Globe, Mail, MapPin, Phone } from 'lucide-react'
import { APK_DOWNLOAD_PATH, LOGO_SRC } from '../constants'

export function SiteFooter() {
  const year = new Date().getFullYear()
  return (
    <footer className="relative border-t border-primary/10 overflow-hidden bg-white dark:bg-card">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary/[0.22] via-primary/[0.08] to-transparent dark:from-primary/[0.25] dark:via-primary/[0.10] dark:to-transparent"
      />
      <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        {/* Top grid — brand column on the left, link columns on the right.
            On mobile everything stacks; from md upwards we use a 12-col
            grid so the brand column can breathe while the link columns
            stay compact. */}
        <div className="grid gap-10 md:grid-cols-12 md:gap-8">
          {/* ── Brand column ── */}
          <div className="md:col-span-5">
            <img
              src={LOGO_SRC}
              alt="Future Protea"
              className="h-20 w-auto sm:h-24 lg:h-28"
            />
            <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
              Future Protea is the end-to-end cricket platform for amateur
              and professional tournaments — fixtures, ball-by-ball live
              scoring, standings, brackets and player analytics in one
              place.
            </p>

            {/* Contact strip — small icons line up with each piece of
                contact info so the column reads as a proper business
                card, not a wall of grey text. */}
            <ul className="mt-6 space-y-2.5 text-sm text-muted-foreground">
              <li className="flex items-start gap-2.5">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <a
                  href="mailto:contact@positronsoftware.co.za"
                  className="hover:text-foreground transition-colors"
                >
                  contact@positronsoftware.co.za
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <a
                  href="tel:+27115754700"
                  className="hover:text-foreground transition-colors"
                >
                  011 575 4700
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>
                  Positron SE — Wrigley Field Building, The Campus 57,
                  Sloane Street, Bryanston, 2021
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <Globe className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <a
                  href="https://www.positronsoftware.co.za"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="hover:text-foreground transition-colors"
                >
                  www.positronsoftware.co.za
                </a>
              </li>
            </ul>
          </div>

          {/* ── Product column ── */}
          <div className="md:col-span-2">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              Product
            </h4>
            <ul className="mt-4 space-y-2.5 text-sm text-muted-foreground">
              <li><a href="#features"     className="hover:text-foreground transition-colors">Features</a></li>
              <li><a href="#mobile"       className="hover:text-foreground transition-colors">Mobile app</a></li>
              <li><a href="#how-it-works" className="hover:text-foreground transition-colors">How it works</a></li>
              <li><a href="#download"     className="hover:text-foreground transition-colors">Download</a></li>
            </ul>
          </div>

          {/* ── Resources column ── */}
          <div className="md:col-span-2">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              Resources
            </h4>
            <ul className="mt-4 space-y-2.5 text-sm text-muted-foreground">
              <li><a href={APK_DOWNLOAD_PATH} download className="hover:text-foreground transition-colors">Android APK</a></li>
              <li><Link to="/login" className="hover:text-foreground transition-colors">Admin login</Link></li>
              <li><a href="mailto:contact@positronsoftware.co.za" className="hover:text-foreground transition-colors">Support</a></li>
            </ul>
          </div>

          {/* ── Company column ── */}
          <div className="md:col-span-3">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              Company
            </h4>
            <ul className="mt-4 space-y-2.5 text-sm text-muted-foreground">
              <li><span className="opacity-70">About</span></li>
              <li><span className="opacity-70">Privacy policy</span></li>
              <li><span className="opacity-70">Terms of service</span></li>
              <li><span className="opacity-70">Contact</span></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar — copyright + release pill on opposite ends.
            Stays centred on mobile so the footer doesn't look
            lopsided at narrow widths. */}
        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t pt-6 text-xs text-muted-foreground sm:flex-row">
          <p>
            © {year} Future Protea. All rights reserved.
          </p>
          <p className="flex items-center gap-2">
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Latest release v1.3.1 · Android 8+
          </p>
        </div>
      </div>
    </footer>
  )
}
